import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';

export interface TotpSetupResponse {
  secret: string;
  otpauthUrl: string;
  qrDataUrl: string;
}

export interface PasskeyDto {
  id: string;
  credentialId: string;
  deviceName: string | null;
  transports: string[];
  createdAt: string;
  lastUsedAt: string | null;
}

export interface MyMfaStatus {
  totp: { enrolled: boolean; confirmedAt: string | null };
  passkeys: PasskeyDto[];
  hasAtLeastOneFactor: boolean;
}

export interface MfaSettings {
  requireMfa: boolean;
  updatedAt: string;
  updatedBy: string | null;
}

const KEYS = {
  myMfa: ['mfa', 'me'] as const,
  settings: ['mfa', 'settings'] as const,
};

/** What MFA factors does the signed-in user have enrolled? */
export function useMyMfa() {
  return useQuery({ queryKey: KEYS.myMfa, queryFn: () => apiGet<MyMfaStatus>('/api/me/mfa') });
}

export function useSetupTotp() {
  return useMutation({
    mutationFn: () => apiPost<TotpSetupResponse>('/api/me/mfa/totp/setup'),
  });
}

export function useConfirmTotp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { code: string }) => apiPost<void>('/api/me/mfa/totp/confirm', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.myMfa }),
  });
}

export function useDisableTotp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete<void>('/api/me/mfa/totp'),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.myMfa }),
  });
}

export function usePasskeyRegistrationOptions() {
  return useMutation({
    mutationFn: () => apiPost<unknown>('/api/me/mfa/passkey/register/options'),
  });
}

export function useVerifyPasskeyRegistration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { response: unknown; deviceName?: string }) =>
      apiPost<PasskeyDto>('/api/me/mfa/passkey/register/verify', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.myMfa }),
  });
}

export function useRemovePasskey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<void>(`/api/me/mfa/passkey/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.myMfa }),
  });
}

export function useMfaSettings() {
  return useQuery({
    queryKey: KEYS.settings,
    queryFn: () => apiGet<MfaSettings>('/api/admin/mfa-settings'),
  });
}

export function useUpdateMfaSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { requireMfa: boolean }) =>
      apiPatch<MfaSettings>('/api/admin/mfa-settings', input),
    onSuccess: (saved) => {
      qc.setQueryData(KEYS.settings, saved);
    },
  });
}
